/*
 * Lemonade Training Oy, copy rights, all right reserved.
 * The app's AI coach insight on the web: the same document, the same rules
 * for when it shows, the same feedback callable (mobile radarSlice + HomeScreen).
 */
import { useCallback, useEffect, useState } from "react";
import { collection, limit, onSnapshot, orderBy, query } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { firebaseDB, firebaseFunctions } from "@lemonadetraining/web-shared";
import {
  isAICoachSuggestion,
  SUGGESTION_FEEDBACK,
  type AICoachFeedbackRequest,
  type AICoachSuggestion,
  type SuggestionFeedback,
} from "@lemonadetraining/contracts";

const DISMISSED_KEY = "lt:ai-coach:dismissed";

function readDismissed(): string[] {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(DISMISSED_KEY) ?? "[]");
    return Array.isArray(parsed) ? parsed.filter((s): s is string => typeof s === "string") : [];
  } catch {
    return [];
  }
}

function writeDismissed(ids: string[]): void {
  try {
    window.localStorage.setItem(DISMISSED_KEY, JSON.stringify(ids.slice(-20)));
  } catch {
    // Storage off: the dismissal still holds for this visit.
  }
}

export interface UseAICoachSuggestionResult {
  /** The newest suggestion the member has not dismissed, or null: hide the card. */
  suggestion: AICoachSuggestion | null;
  sendFeedback: (suggestion: AICoachSuggestion, feedback: SuggestionFeedback) => void;
  dismiss: (suggestion: AICoachSuggestion) => void;
}

const recordFeedback = (suggestionID: string, feedback: SuggestionFeedback) =>
  httpsCallable<AICoachFeedbackRequest, void>(
    firebaseFunctions,
    "recordAICoachFeedback"
  )({ suggestionID, feedback }).catch((err) => {
    // eslint-disable-next-line no-console
    console.warn("[AICoach] feedback not recorded", err);
  });

export function useAICoachSuggestion(uid: string | null): UseAICoachSuggestionResult {
  const [latest, setLatest] = useState<AICoachSuggestion | null>(null);
  const [dismissed, setDismissed] = useState<string[]>(readDismissed);

  useEffect(() => {
    setLatest(null);
    if (!uid) return undefined;
    const newest = query(
      collection(firebaseDB, "Users", uid, "AISuggestions"),
      orderBy("createdAt", "desc"),
      limit(1)
    );
    return onSnapshot(
      newest,
      (snap) => {
        const doc = snap.docs[0];
        const data = doc ? { ...doc.data(), id: doc.id } : null;
        setLatest(isAICoachSuggestion(data) ? data : null);
      },
      (err) => {
        // No insight is the same as no card; the page must not break over it.
        // eslint-disable-next-line no-console
        console.warn("[AICoach] suggestion listener failed", err);
        setLatest(null);
      }
    );
  }, [uid]);

  const dismiss = useCallback((s: AICoachSuggestion) => {
    setDismissed((prev) => {
      const next = prev.includes(s.id) ? prev : [...prev, s.id];
      writeDismissed(next);
      return next;
    });
    // A thumbs rating already given stays the recorded signal.
    if (s.feedback == null) void recordFeedback(s.id, SUGGESTION_FEEDBACK.DISMISSED);
  }, []);

  const sendFeedback = useCallback((s: AICoachSuggestion, feedback: SuggestionFeedback) => {
    void recordFeedback(s.id, feedback);
  }, []);

  const suggestion = latest && !latest.dismissed && !dismissed.includes(latest.id) ? latest : null;
  return { suggestion, sendFeedback, dismiss };
}
